import React from "react";
import { StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { MeetingsTabKey } from "../MeetingsTabsView.types";
import { FeedbackFabButton } from "../../../../components/FeedbackFabButton";

type Props = {
  activeTab: MeetingsTabKey;
  onChangeTab: (tab: MeetingsTabKey) => void;
  onPressFilter?: () => void;
  onPressCreateMeeting?: () => void;
  onPressConversationStarters?: () => void;
  conversationCount?: number;
  renderAllMeetings: React.ReactNode;
  renderMyMeetings: React.ReactNode;
};

export default function MeetingsTabsViewMobile({
  activeTab,
  onChangeTab,
  onPressCreateMeeting,
  onPressConversationStarters,
  renderAllMeetings,
  renderMyMeetings,
}: Props) {
  // Android draws edge-to-edge, so the system navigation bar overlaps the
  // bottom of the screen; lift the floating buttons above it (0 on web).
  const { bottom: bottomInset } = useSafeAreaInsets();

  return (
    <View style={styles.container}>
      <View style={styles.tabContainer}>
        <View style={styles.tabsWrapper}>
          <TouchableOpacity
            style={[
              styles.tab,
              styles.leftTab,
              activeTab === "AllMeetings" && styles.activeTab,
            ]}
            onPress={() => onChangeTab("AllMeetings")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "AllMeetings" && styles.activeTabText,
              ]}
            >
              Available Conversations
            </Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={[
              styles.tab,
              styles.rightTab,
              activeTab === "MyMeetings" && styles.activeTab,
            ]}
            onPress={() => onChangeTab("MyMeetings")}
          >
            <Text
              style={[
                styles.tabText,
                activeTab === "MyMeetings" && styles.activeTabText,
              ]}
            >
              My Conversations
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {onPressConversationStarters && (
        <TouchableOpacity
          style={styles.startersRow}
          onPress={onPressConversationStarters}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Browse conversation starters"
        >
          <Ionicons name="chatbubbles-outline" size={16} color="#3A6FB1" />
          <Text style={styles.startersText}>
            Not sure what to say? Browse conversation starters
          </Text>
          <Ionicons name="chevron-forward" size={14} color="#3A6FB1" />
        </TouchableOpacity>
      )}

      <View style={styles.content}>
        {activeTab === "AllMeetings" ? renderAllMeetings : renderMyMeetings}
      </View>

      <FeedbackFabButton
        style={[
          styles.feedbackButton,
          { bottom: styles.feedbackButton.bottom + bottomInset },
        ]}
      />

      {onPressCreateMeeting && (
        <TouchableOpacity
          style={[
            styles.floatingCreateButton,
            { bottom: styles.floatingCreateButton.bottom + bottomInset },
          ]}
          onPress={onPressCreateMeeting}
          activeOpacity={0.8}
          accessibilityRole="button"
          accessibilityLabel="Create conversation"
        >
          <Ionicons name="add" size={28} color="#FFFFFF" />
        </TouchableOpacity>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F6F7F9",
  },
  tabContainer: {
    width: "100%",
    height: 62,
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: "#F6F7F9",
    alignItems: "center",
    justifyContent: "center",
  },
  tabsWrapper: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    maxWidth: 343,
    height: 46,
  },
  tab: {
    flex: 1,
    height: 46,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: "#D8DBE1",
    backgroundColor: "#FFFFFF",
  },
  leftTab: {
    borderTopLeftRadius: 16,
    borderBottomLeftRadius: 16,
    borderRightWidth: 0,
  },
  rightTab: {
    borderTopRightRadius: 16,
    borderBottomRightRadius: 16,
  },
  activeTab: {
    backgroundColor: "#3A6FB1",
  },
  tabText: {
    fontSize: 11,
    lineHeight: 14,
    fontFamily: "Lexend",
    textTransform: "uppercase",
    color: "#2B2B2F",
  },
  activeTabText: {
    color: "#F6F7F9",
  },
  startersRow: {
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    maxWidth: 343,
    alignSelf: "center",
    marginBottom: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderRadius: 12,
    backgroundColor: "#EAF0F8",
    gap: 8,
  },
  startersText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    fontFamily: "Lexend",
    color: "#3A6FB1",
  },
  content: {
    flex: 1,
  },
  // Bottom-left so it never collides with the create FAB on the right; vertically
  // centred on the 56px create button.
  feedbackButton: {
    bottom: 37,
    right: undefined,
    left: 24,
  },
  floatingCreateButton: {
    position: "absolute",
    bottom: 24,
    right: 24,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: "#3A6FB1",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 3 },
    shadowOpacity: 0.2,
    shadowRadius: 5,
    elevation: 6,
  },
});
